/**
 * AuditFilters Component
 *
 * Feature: 123-audit-log-viewer
 * Description: Filter panel for system and auth audit log pages
 * Constitution: v1.12.0
 * Tasks: T021 (system filters), T023 (auth filters)
 */

import React, { useState, useCallback } from 'react';
import { Filter, X } from 'lucide-react';
import { FilterBar, Input, Select, Button } from '../../../components/ui';
import { useEntityTypes, useActions } from '../../../hooks/useAuditQuery';
import { humanizeAuditLabel } from './auditLabels';
import type {
  SystemAuditLogFilters,
  AuthAuditLogFilters,
  AuditAction,
  AuthEventType,
} from '../../../types/audit';

type FilterMode = 'system' | 'auth';

interface AuditFiltersProps {
  mode: FilterMode;
  initialFilters: SystemAuditLogFilters | AuthAuditLogFilters;
  onApply: (filters: SystemAuditLogFilters | AuthAuditLogFilters) => void;
}

const ACTION_OPTIONS: { value: AuditAction; label: string }[] = [
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

const EVENT_TYPE_OPTIONS: { value: AuthEventType; label: string }[] = [
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'token_refresh', label: 'Token Refresh' },
  { value: 'session_expired', label: 'Session Expired' },
  { value: 'password_change', label: 'Password Change' },
  { value: 'mfa_challenge', label: 'MFA Challenge' },
];

const RESULT_OPTIONS = [
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
];

function toDateInputValue(isoString: string): string {
  return isoString.substring(0, 16); // YYYY-MM-DDTHH:mm
}

export default function AuditFilters({ mode, initialFilters, onApply }: AuditFiltersProps) {
  const [startDate, setStartDate] = useState(toDateInputValue(initialFilters.startDate));
  const [endDate, setEndDate] = useState(toDateInputValue(initialFilters.endDate));
  const [userEmail, setUserEmail] = useState(initialFilters.userEmail || '');

  // System-specific
  const [action, setAction] = useState((initialFilters as SystemAuditLogFilters).action || '');
  const [entityType, setEntityType] = useState(
    (initialFilters as SystemAuditLogFilters).entityType || ''
  );
  const [search, setSearch] = useState((initialFilters as SystemAuditLogFilters).search || '');

  // Auth-specific
  const [eventType, setEventType] = useState(
    (initialFilters as AuthAuditLogFilters).eventType || ''
  );
  const [result, setResult] = useState((initialFilters as AuthAuditLogFilters).result || '');

  // Entity types + actions for the dropdowns — dynamic (distinct values actually in the log) so the
  // backup/restore/plan/schedule/settings entries (and any future ones) appear automatically.
  const { data: entityTypes } = useEntityTypes();
  const { data: actions } = useActions();

  const countActiveFilters = useCallback(() => {
    let count = 0;
    if (userEmail) count++;
    if (mode === 'system') {
      if (action) count++;
      if (entityType) count++;
      if (search) count++;
    } else {
      if (eventType) count++;
      if (result) count++;
    }
    return count;
  }, [mode, userEmail, action, entityType, search, eventType, result]);

  const handleApply = useCallback(() => {
    const base = {
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      ...(userEmail ? { userEmail } : {}),
    };

    if (mode === 'system') {
      const filters: SystemAuditLogFilters = {
        ...base,
        ...(action ? { action: action as AuditAction } : {}),
        ...(entityType ? { entityType: entityType as SystemAuditLogFilters['entityType'] } : {}),
        ...(search ? { search } : {}),
      };
      onApply(filters);
    } else {
      const filters: AuthAuditLogFilters = {
        ...base,
        ...(eventType ? { eventType: eventType as AuthEventType } : {}),
        ...(result ? { result: result as 'success' | 'failed' } : {}),
      };
      onApply(filters);
    }
  }, [mode, startDate, endDate, userEmail, action, entityType, search, eventType, result, onApply]);

  const handleClear = useCallback(() => {
    const now = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    setStartDate(toDateInputValue(weekAgo.toISOString()));
    setEndDate(toDateInputValue(now.toISOString()));
    setUserEmail('');
    setAction('');
    setEntityType('');
    setSearch('');
    setEventType('');
    setResult('');

    const defaultFilters = {
      startDate: weekAgo.toISOString(),
      endDate: now.toISOString(),
    };
    onApply(defaultFilters as SystemAuditLogFilters | AuthAuditLogFilters);
  }, [onApply]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleApply();
    },
    [handleApply]
  );

  const activeCount = countActiveFilters();

  return (
    <FilterBar>
      <div style={{ width: 220 }}>
        <Input
          label="Start Date"
          type="datetime-local"
          inputSize="sm"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          isFullWidth
        />
      </div>
      <div style={{ width: 220 }}>
        <Input
          label="End Date"
          type="datetime-local"
          inputSize="sm"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          isFullWidth
        />
      </div>
      <div style={{ width: 200 }}>
        <Input
          label="User Email"
          inputSize="sm"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Filter by email"
          isFullWidth
        />
      </div>

      {mode === 'system' && (
        <>
          <div style={{ width: 200 }}>
            <Select
              label="Action"
              selectSize="sm"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              isFullWidth
            >
              <option value="">All Actions</option>
              {/* Dynamic: every action recorded in the log (falls back to the basic set if empty). */}
              {(actions && actions.length ? actions : ACTION_OPTIONS.map((o) => o.value)).map((a) => (
                <option key={a} value={a}>
                  {humanizeAuditLabel(a)}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ width: 200 }}>
            <Select
              label="Entity Type"
              selectSize="sm"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              isFullWidth
            >
              <option value="">All Entity Types</option>
              {(entityTypes || []).map((type) => (
                <option key={type} value={type}>
                  {humanizeAuditLabel(type)}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ width: 220 }}>
            <Input
              label="Search"
              inputSize="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Entity name or ID..."
              isFullWidth
            />
          </div>
        </>
      )}

      {mode === 'auth' && (
        <>
          <div style={{ width: 160 }}>
            <Select
              label="Event Type"
              selectSize="sm"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              isFullWidth
            >
              <option value="">All</option>
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ width: 140 }}>
            <Select
              label="Result"
              selectSize="sm"
              value={result}
              onChange={(e) => setResult(e.target.value)}
              isFullWidth
            >
              <option value="">All</option>
              {RESULT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        </>
      )}

      <div className="flex items-center gap-2">
        <div className="relative inline-flex">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Filter className="h-4 w-4" />}
            onClick={handleApply}
          >
            Apply Filters
          </Button>
          {activeCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[10px] font-semibold text-white">
              {activeCount}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<X className="h-4 w-4" />}
          onClick={handleClear}
        >
          Clear Filters
        </Button>
      </div>
    </FilterBar>
  );
}
